import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {addMember, type CompanyRole} from '@/entities/member/api';
import {ApiError} from '@/shared/api';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {AppInput} from '@/shared/ui/input';
import {ChoiceChips, Segmented} from '@/shared/ui/segmented';
import {Sheet} from '@/shared/ui/sheet';
import {AppText} from '@/shared/ui/text';
import {spacing} from '@/shared/ui/theme';

/**
 * Add somebody to the company — and, optionally, give them a role in the same call.
 *
 * Two ways in, because the server takes exactly two and refuses a request that names
 * neither ("Provide member_account_id or new_member"):
 *
 *   BY ACCOUNT — the person already has a SamVision account; you paste its id. This is
 *                the path for someone who is already an employee elsewhere on the
 *                platform.
 *   NEW        — create the account inline. A password is REQUIRED by the server here,
 *                so the form asks for one and says out loud that it has to be passed to
 *                the person by hand: nothing is emailed.
 *
 * ⚠️ The role is granted BEST-EFFORT, in a second step the server does after the
 * membership row is written — and a failure there is logged, not returned. So a member
 * can come back added-but-roleless with a 200. The list re-reads after this closes,
 * and the role chips on each row are how it gets fixed without re-adding anyone.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(3)},
  block: {gap: spacing(2)},
});

const ROLES: readonly CompanyRole[] = ['admin', 'accountant', 'owner'];

export function AddMemberSheet({
  companyId,
  visible,
  onClose,
}: {
  companyId: string;
  visible: boolean;
  onClose: () => void;
}) {
  const {t} = useTranslation();
  const qc = useQueryClient();

  const [mode, setMode] = useState<'existing' | 'new'>('new');
  const [accountId, setAccountId] = useState('');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [position, setPosition] = useState('');
  const [role, setRole] = useState<CompanyRole | ''>('');

  const reset = () => {
    setAccountId('');
    setEmail('');
    setFullName('');
    setPhone('');
    setPassword('');
    setPosition('');
    setRole('');
  };

  const add = useMutation({
    mutationFn: () =>
      addMember(companyId, {
        accountId: mode === 'existing' ? accountId.trim() : undefined,
        newMember:
          mode === 'new'
            ? {
                email: email.trim() || undefined,
                phone: phone.trim() || undefined,
                full_name: fullName.trim() || undefined,
                password,
              }
            : undefined,
        position: position.trim() || undefined,
        role: role || undefined,
      }),
    onSuccess: async () => {
      reset();
      await qc.invalidateQueries({queryKey: ['members', companyId]});
      onClose();
    },
  });

  const canSubmit =
    mode === 'existing'
      ? accountId.trim().length > 0
      : password.length > 0 && (email.trim().length > 0 || phone.trim().length > 0);

  return (
    <Sheet
      visible={visible}
      onClose={() => {
        add.reset();
        onClose();
      }}
      title={t('members.add')}
      subtitle={t('members.addHint')}
      testID="add-member-sheet">
      <View style={styles.root}>
        <Segmented
          value={mode}
          onChange={setMode}
          testID="member-mode"
          options={[
            {value: 'new', label: t('members.modeNew')},
            {value: 'existing', label: t('members.modeExisting')},
          ]}
        />

        {mode === 'existing' ? (
          <AppInput
            label={t('members.accountId')}
            value={accountId}
            onChangeText={setAccountId}
            autoCapitalize="none"
            hint={t('members.accountIdHint')}
            testID="member-account-id"
          />
        ) : (
          <>
            <AppInput
              label={t('profile.fullName')}
              value={fullName}
              onChangeText={setFullName}
              testID="member-full-name"
            />
            <AppInput
              label={t('profile.email')}
              value={email}
              onChangeText={setEmail}
              autoCapitalize="none"
              keyboardType="email-address"
              testID="member-email"
            />
            <AppInput
              label={t('account.phone')}
              value={phone}
              onChangeText={setPhone}
              keyboardType="phone-pad"
              testID="member-phone"
            />
            <AppInput
              label={t('security.newPassword')}
              value={password}
              onChangeText={setPassword}
              secure
              hint={t('members.passwordHint')}
              showPasswordLabel={t('a11y.showPassword')}
              hidePasswordLabel={t('a11y.hidePassword')}
              testID="member-password"
            />
          </>
        )}

        <AppInput
          label={t('members.position')}
          value={position}
          onChangeText={setPosition}
          testID="member-position"
        />

        <View style={styles.block}>
          <AppText variant="label">{t('members.roleLabel')}</AppText>
          <AppText variant="caption">{t('members.roleHint')}</AppText>
          <ChoiceChips
            value={role}
            onChange={value => setRole(value as CompanyRole | '')}
            testID="member-role"
            options={[
              {value: '', label: t('members.role.member')},
              ...ROLES.map(code => ({
                value: code,
                label: t(`members.role.${code}`, code),
              })),
            ]}
          />
        </View>

        {add.isError ? (
          <Banner
            tone="error"
            title={
              add.error instanceof ApiError ? add.error.message : t('errors.generic')
            }
          />
        ) : null}

        <AppButton
          title={t('members.add')}
          disabled={!canSubmit}
          loading={add.isPending}
          onPress={() => add.mutate()}
          testID="member-add-submit"
        />
      </View>
    </Sheet>
  );
}
